/**
 * Metin girdisi, seçim ve çok satırlı girdi — **parmak** mertebesi.
 *
 * Etiket bileşenin İÇİNDE: bir girdi etiketsiz kullanılamamalı (erişilebilirlik)
 * ve etiket-girdi arası boşluk her yerde aynı olmalı. `--space-1` (4px) parmak
 * içi boşluktur; etiket ile girdi tek bir "el" oluşturur.
 */

import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';

/**
 * Tüm form alanlarının tek sınıfı — Ayarlar dahil her ekran bunu kullanır.
 * Ölçüldü (Vuexy 10.11): input padding .426rem/.9375rem → 8px/12px (4px ölçeğinde); odakta
 * kenarlık vurgu rengi + yumuşak gölge (2px ring yerine — ring, alanı "seçili
 * kutu" gibi gösteriyordu).
 */
export const ALAN_SINIFI = cn(
  'w-full rounded-[var(--radius-sm)] border border-[var(--line-strong)]',
  'bg-[var(--surface)] text-[var(--ink)] px-3 py-2 text-sm',
  'placeholder:text-[var(--ink-faint)]',
  'transition-[border-color,box-shadow] duration-150',
  'focus:border-[var(--accent)] focus:outline-none focus:shadow-[var(--focus-ring)]',
  'disabled:cursor-not-allowed disabled:bg-[var(--surface-sunken)] disabled:opacity-70'
);

interface EtiketliProps {
  etiket: string;
  /** Girdinin altındaki açıklama — hata değil, yönlendirme. */
  ipucu?: string;
  /** Hata metni: doluysa kenarlık ve mesaj kırmızıya döner. */
  hata?: string;
}

/** Etiket, alan ve açıklamayı birbirine bağlayan kimlikler. */
function useAlanKimligi(id: string | undefined, aciklamaVar: boolean) {
  const uretilen = useId();
  const alanId = id ?? uretilen;
  return { alanId, aciklamaId: aciklamaVar ? `${alanId}-aciklama` : undefined };
}

interface AlanSarmalProps {
  alanId: string;
  aciklamaId: string | undefined;
  etiket: string;
  ipucu: string | undefined;
  hata: string | undefined;
  children: ReactNode;
}

/**
 * Etiket + alan + açıklama iskeleti — üç girdi türünün ortak yapısı.
 * Ölçülen tekrar: aynı 12 satır Girdi, Secim ve CokSatir'da üç kez vardı ve
 * Secim'de `aria-describedby` unutulmuştu; tek iskelet bunu imkânsız kılar.
 */
function AlanSarmal({ alanId, aciklamaId, etiket, ipucu, hata, children }: AlanSarmalProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={alanId} className="text-xs font-medium text-[var(--ink-muted)]">
        {etiket}
      </label>
      {children}
      {(ipucu || hata) && (
        <p
          id={aciklamaId}
          className={cn('text-xs', hata ? 'text-[var(--state-bad)]' : 'text-[var(--ink-faint)]')}
        >
          {hata ?? ipucu}
        </p>
      )}
    </div>
  );
}

export interface GirdiProps extends InputHTMLAttributes<HTMLInputElement>, EtiketliProps {}

export function Girdi({ etiket, ipucu, hata, className, id, ...kalan }: GirdiProps) {
  const { alanId, aciklamaId } = useAlanKimligi(id, Boolean(ipucu || hata));

  return (
    <AlanSarmal alanId={alanId} aciklamaId={aciklamaId} etiket={etiket} ipucu={ipucu} hata={hata}>
      <input
        id={alanId}
        // Ekran okuyucu hatayı ve ipucunu girdinin parçası olarak okusun.
        aria-describedby={aciklamaId}
        aria-invalid={hata ? true : undefined}
        className={cn(ALAN_SINIFI, hata && 'border-[var(--state-bad)]', className)}
        {...kalan}
      />
    </AlanSarmal>
  );
}

export interface SecimProps extends SelectHTMLAttributes<HTMLSelectElement>, EtiketliProps {
  children: ReactNode;
}

export function Secim({ etiket, ipucu, hata, className, id, children, ...kalan }: SecimProps) {
  const { alanId, aciklamaId } = useAlanKimligi(id, Boolean(ipucu || hata));

  return (
    <AlanSarmal alanId={alanId} aciklamaId={aciklamaId} etiket={etiket} ipucu={ipucu} hata={hata}>
      <select
        id={alanId}
        aria-describedby={aciklamaId}
        aria-invalid={hata ? true : undefined}
        className={cn(ALAN_SINIFI, 'pr-6', hata && 'border-[var(--state-bad)]', className)}
        {...kalan}
      >
        {children}
      </select>
    </AlanSarmal>
  );
}

export interface CokSatirProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, EtiketliProps {}

/**
 * Çok satırlı girdi — şablon metni gibi uzun içerik.
 * `forwardRef`: şablon editörü imleç konumuna yer tutucu eklemek için
 * elemana erişmek zorunda; state üzerinden imleç bilinemez.
 */
export const CokSatir = forwardRef<HTMLTextAreaElement, CokSatirProps>(function CokSatir(
  { etiket, ipucu, hata, className, id, ...kalan },
  ref
) {
  const { alanId, aciklamaId } = useAlanKimligi(id, Boolean(ipucu || hata));

  return (
    <AlanSarmal alanId={alanId} aciklamaId={aciklamaId} etiket={etiket} ipucu={ipucu} hata={hata}>
      <textarea
        ref={ref}
        id={alanId}
        aria-describedby={aciklamaId}
        aria-invalid={hata ? true : undefined}
        className={cn(
          ALAN_SINIFI,
          'min-h-24 resize-y leading-relaxed',
          hata && 'border-[var(--state-bad)]',
          className
        )}
        {...kalan}
      />
    </AlanSarmal>
  );
});
